"use client";

import { AnimatePresence } from "framer-motion";
import { useChat } from "@ai-sdk/react";
import { SourceDocumentUIPart, SourceUrlUIPart, type UIMessage } from "ai";
import type { NoxusUIMessage } from "./noxus-message-types";
import { createFinalAnswerReplay } from "./finalAnswerReplay";
import { shouldSendAutomatically } from "./autoSend";
import { authHeaders, type ChatAuth } from "./chatAuth";
import { useApproval } from "./approval/useApproval";
import { ApprovalComposer } from "./approval/ApprovalComposer";
import { QuestionComposer } from "./questions/QuestionComposer";
import { useQuestions } from "./questions/useQuestions";
import {
  QUESTION_REJECTED_CODE,
  QUESTION_TOOL_NAME,
} from "./questions/questionTypes";
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  FormEvent,
  createContext,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { genieDraftAtom } from "@/store/genie-atoms";
import { useTranslations } from "next-intl";
import { useFirstTokenTiming } from "./useFirstTokenTiming";
import { useLocalizedToolLabels } from "@/i18n/toolLabels";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { NoxusPromptInput } from "@/components/NewAndIncredibleChatWidget/PromptInput";
import {
  PromptInput,
  PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import { Conversation, ConversationContent } from "../ai-elements/conversation";
import type { StickToBottomContext } from "use-stick-to-bottom";
import { isTurnUnstarted, thinkingLabel } from "./ThinkingLine";
import { ScrollToLatest } from "./ScrollToLatest";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import {
  Assistant,
  ConversationWithMessages,
  PublicAssistant,
} from "@/generated";
import { SpotToast } from "../SpotToast";
import { SourcesDrawer } from "./NewSourcesDrawer";
import { NewChatEmptyState } from "./NewChatEmptyState";
import {
  PublicConversationService,
  isSignedEmbedToken,
  type PublicAuth,
} from "@/services/public/Conversation";
import { ChatMessageList } from "./ChatMessageList";
import { useEarlierMessages } from "./useEarlierMessages";
import { inLatestTurn, latestTurnOnly } from "./latestTurnOnly";
import { DebugSheetHost } from "./DebugSheetHost";
import { cn } from "@/lib/utils";
import {
  useChatDisplayOptions,
  ChatDisplayOptionsContext,
} from "./useChatDisplayOptions";
import { useToolEffects } from "@/lib/genie/tools/effects/useToolEffects";
import {
  isGatedForApproval,
  runGenieToolCall,
  type GenieToolCall,
  type ReportToolResult,
} from "./runGenieToolCall";
import type { GenieToolDefinition } from "@/lib/genie";
import {
  foldAssistantMessages,
  getTextFromParts,
  withStoredFeedback,
} from "./utils";
import { hasPersistableTurnContent } from "./chainHeaderText";
import { ReconnectAwareChatTransport } from "./reconnectAwareChatTransport";
import { limitAwareFetch } from "@/utils/limitAwareFetch";
import { ReconnectBudget } from "./reconnectBudget";
import { genieWroteConversationAtom } from "@/store/conversational";
import { useAtomValue } from "jotai";
import { isBillingLimitError } from "@/utils/billingModalController";
import { isOutOfStorageError } from "@/utils/storageLimitController";

const chatColumnClasses = (opts: {
  isCompact?: boolean;
  isGenie?: boolean;
  isTextExpanded?: boolean;
  isWidget?: boolean;
}) =>
  cn(
    "w-full transition-all duration-300",
    opts.isTextExpanded ? "max-w-full" : "max-w-3xl",
    opts.isCompact || opts.isGenie || opts.isWidget ? "px-4" : "px-6"
  );

interface INewAndIncredibleChatWidget {
  conversationId: string;
  groupId: string;
  assistantId: string;
  sourcesMode?: "inline" | "floating";
  publicMetadata?: {
    group_id: string;
    token: string;
    deployment_id?: string;
    // noxus_user embeds: the user's Noxus access token, sent as a Bearer
    // header so the agent runs as the real Noxus user.
    auth_token?: string;
  };
  // Invoked before a reconnect attempt so the host can mint a fresh embed token
  // when the current one has expired.
  onAuthRetry?: () => void;
  sourcesClassName?: string;
  isCompact?: boolean;
  isTextExpanded?: boolean;
  configChanged?: boolean;
  onStartNewChat?: () => void;
  maintainFooterOnLoad?: boolean;
  transportBody?: () => Record<string, unknown> & {
    frontend_tools?: GenieToolDefinition[];
  };
  // Overrides the default assistant avatar in the empty state (e.g. deployment logo).
  agentIconUrl?: string;
  // Caller-controlled yellow banner + input disable (non-destructive,
  // distinct from the safety lockedMessage). Set from the agent builder
  // when the workspace has no dispatchable models so users get the
  // signal before they hit "send" and see a generic error.
  warningMessage?: string | null;
  isGenie?: boolean;
  showPoweredBy?: boolean;
  // Public widget only: gates the per-message thumbs feedback controls. The
  // internal/authenticated chat always shows them (see useChatDisplayOptions).
  showMessageFeedback?: boolean;
  // View-only mode: hides the composer and shows a "Resume conversation" bar
  // instead. Used when reviewing a past conversation so a stray keystroke
  // can't append a message. ``onResume`` leaves read-only mode.
  readOnly?: boolean;
  onResume?: () => void;
  offerTestThisReply?: boolean;

  loadingFallback?: React.ReactNode;
}

interface IInnerNewAndIncredibleChatWidget extends INewAndIncredibleChatWidget {
  conversationData?: ConversationWithMessages;
  assistantData?: Assistant | PublicAssistant;
  refetchConversation: () => Promise<ConversationWithMessages | undefined>;
}

export const NewAndIncredibleChatWidget = ({
  conversationId,
  groupId,
  assistantId,
  sourcesMode = "inline",
  publicMetadata,
  onAuthRetry,
  sourcesClassName,
  isCompact,
  isTextExpanded,
  configChanged,
  onStartNewChat,
  maintainFooterOnLoad,
  transportBody,
  agentIconUrl,
  warningMessage,
  isGenie,
  showPoweredBy,
  showMessageFeedback,
  readOnly,
  onResume,
  offerTestThisReply,
  loadingFallback,
}: INewAndIncredibleChatWidget) => {
  const {
    data: privateConversationData,
    isLoading: isConversationLoading,
    isFetching: isConversationFetching,
    refetch: refetchPrivateConversation,
  } = useRemoteData(
    DATA_KEYS.CONVERSATION,
    {
      path: {
        group_id: groupId,
        conversation_id: conversationId,
      },
      query: { paged: true },
    },
    {
      enabled: !!conversationId && !!groupId && !publicMetadata,
      // A read-only view has no stream of its own, so turns written since the
      // last fetch (an agent test's run) only show if it refetches on mount.
      staleTime: readOnly ? 0 : 30000,
    }
  );

  const {
    data: publicConversationData,
    isLoading: isPublicConversationLoading,
    isFetching: isPublicConversationFetching,
    refetch: refetchPublicConversation,
  } = useQuery({
    queryKey: [DATA_KEYS.CONVERSATION, conversationId, "public"],
    queryFn: () =>
      PublicConversationService.getConversation({
        spotGroupId: groupId,
        conversationId: conversationId,
        assistantId: assistantId,
        token: publicMetadata?.token || "",
        deploymentId: publicMetadata?.deployment_id,
        authToken: publicMetadata?.auth_token,
        paged: true,
      }),
    enabled:
      !!conversationId &&
      !!groupId &&
      !!publicMetadata &&
      !!publicMetadata?.token,
  });

  const conversationData = publicMetadata
    ? publicConversationData
    : privateConversationData;

  const { data: privateAssistantData, isLoading: isAssistantLoading } =
    useRemoteData(
      DATA_KEYS.ASSISTANT_ID,
      {
        path: {
          group_id: groupId,
          assistant_id: assistantId,
        },
      },
      {
        enabled: !!groupId && !!assistantId && !publicMetadata,
      }
    );

  const { data: publicAssistantData, isLoading: isPublicAssistantLoading } =
    useQuery({
      queryKey: [DATA_KEYS.ASSISTANT_ID, assistantId, "public"],
      queryFn: () =>
        PublicConversationService.getAssistant({
          spotGroupId: groupId,
          assistantId: assistantId,
          token: publicMetadata?.token || "",
          deploymentId: publicMetadata?.deployment_id,
          authToken: publicMetadata?.auth_token,
        }),
      enabled:
        !!groupId &&
        !!assistantId &&
        !!publicMetadata &&
        !!publicMetadata?.token,
    });

  const assistantData = publicMetadata
    ? publicAssistantData?.data
    : privateAssistantData;

  // Once the chat widget has rendered with fresh data, useChat owns the
  // conversation state internally (messages prop is only used on mount).
  // After that point we never unmount for a refetch — doing so would kill
  // the SSE stream and cause the streaming message to disappear.
  const hasRenderedChat = useRef(false);

  const isLoading =
    isConversationLoading ||
    isPublicConversationLoading ||
    isAssistantLoading ||
    isPublicAssistantLoading;
  const isFetching = isConversationFetching || isPublicConversationFetching;

  if (isLoading || (isFetching && !hasRenderedChat.current)) {
    if (maintainFooterOnLoad && !readOnly) {
      return (
        <div className="flex flex-col flex-1 justify-end items-center">
          <div
            className={cn(
              chatColumnClasses({
                isCompact,
                isGenie,
                isTextExpanded,
                isWidget: !!publicMetadata,
              }),
              "py-0 bg-background self-center"
            )}
          >
            <NoxusPromptInput
              text={""}
              setText={() => {}}
              status="ready"
              handleSubmit={() => {}}
              assistantData={assistantData}
              groupId=""
              conversationId=""
              configChanged={false}
              onStartNewChat={onStartNewChat}
              isPublic={!!publicMetadata}
              isGenie={isGenie}
              warningMessage={warningMessage}
              showPoweredBy={showPoweredBy}
            />
          </div>
        </div>
      );
    }
    return loadingFallback ?? null;
  }

  hasRenderedChat.current = true;

  return (
    <InnerNewAndIncredibleChatWidget
      conversationId={conversationId}
      groupId={groupId}
      assistantId={assistantId}
      sourcesMode={sourcesMode}
      publicMetadata={publicMetadata}
      onAuthRetry={onAuthRetry}
      conversationData={conversationData}
      refetchConversation={async () =>
        (publicMetadata
          ? await refetchPublicConversation()
          : await refetchPrivateConversation()
        ).data
      }
      assistantData={assistantData}
      sourcesClassName={sourcesClassName}
      isCompact={isCompact}
      isTextExpanded={isTextExpanded}
      configChanged={configChanged}
      onStartNewChat={onStartNewChat}
      transportBody={transportBody}
      agentIconUrl={agentIconUrl}
      warningMessage={warningMessage}
      isGenie={isGenie}
      showPoweredBy={showPoweredBy}
      showMessageFeedback={showMessageFeedback}
      readOnly={readOnly}
      onResume={onResume}
      offerTestThisReply={offerTestThisReply}
    />
  );
};

export const ChatWidgetContext = createContext<{
  isPublic: boolean;
  publicAuth?: PublicAuth;
}>({
  isPublic: false,
});

const InnerNewAndIncredibleChatWidget = ({
  conversationId,
  groupId,
  assistantId,
  sourcesMode = "inline",
  publicMetadata,
  onAuthRetry,
  conversationData,
  assistantData,
  sourcesClassName,
  isCompact,
  isTextExpanded,
  configChanged,
  onStartNewChat,
  transportBody,
  agentIconUrl,
  warningMessage,
  isGenie,
  showPoweredBy,
  showMessageFeedback,
  readOnly,
  onResume,
  offerTestThisReply,
  refetchConversation,
}: IInnerNewAndIncredibleChatWidget) => {
  const t = useTranslations("widget.status");
  const tQuestions = useTranslations("widget.questions");
  const [text, setText] = useState("");
  const [genieDraft, setGenieDraft] = useAtom(genieDraftAtom);
  const [draftFocus, setDraftFocus] = useState(0);
  useEffect(() => {
    if (isGenie && genieDraft) {
      setText(genieDraft);
      setGenieDraft(null);
      setDraftFocus((n) => n + 1);
    }
  }, [isGenie, genieDraft, setGenieDraft]);
  const [sourcesDrawerOpen, setSourcesDrawerOpen] = useState<
    (SourceUrlUIPart | SourceDocumentUIPart)[] | null
  >(null);
  const [todoPanelOpen, setTodoPanelOpen] = useState(false);
  // Sticky guardrail lock — initialised from the conversation GET payload
  // and updated when a ``data-conversation_locked`` chunk arrives from the
  // streaming agent. Once locked, the prompt input stays disabled for the
  // rest of the conversation; users start a fresh conversation to unlock.
  const [lockedMessage, setLockedMessage] = useState<string | null>(
    (
      conversationData as
        | { is_locked?: boolean; lock_message?: string }
        | undefined
    )?.is_locked
      ? ((conversationData as { lock_message?: string } | undefined)
          ?.lock_message ?? t("conversationLocked"))
      : null
  );
  const queryClient = useQueryClient();

  // Extract todos from conversation data — memoize to stabilize useEffect dependency
  const rawTodos = conversationData?.todos;
  const todos = useMemo(
    () =>
      (rawTodos ?? []) as Array<{
        id?: string;
        content: string;
        status: string;
      }>,
    [rawTodos]
  );

  // Auto-show todo panel when todos change
  const prevTodosHash = useRef("");
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    const hash = JSON.stringify(todos);
    const changed =
      hash !== prevTodosHash.current &&
      prevTodosHash.current !== "" &&
      todos.length > 0;
    prevTodosHash.current = hash;
    if (changed) {
      setTodoPanelOpen(true);
      autoOpenedRef.current = true;
      const timer = setTimeout(() => {
        if (autoOpenedRef.current) {
          setTodoPanelOpen(false);
          autoOpenedRef.current = false;
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [todos]);

  // When user manually interacts with panel, disable auto-close
  const handleTodoPanelOpenChange = useCallback((open: boolean) => {
    setTodoPanelOpen(open);
    if (open) autoOpenedRef.current = false;
  }, []);

  const invalidateConversations = useCallback(() => {
    if (!publicMetadata) {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.CONVERSATIONS] });
      // Also refetch single conversation to get fresh todos
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.CONVERSATION] });
    }
  }, [publicMetadata, queryClient]);

  const initialMessages = useMemo(
    () => storedUIMessages(conversationData),
    [conversationData]
  );

  // A signed embed JWT travels in the x-embed-token header, not the URL.
  const isSignedEmbed =
    !!publicMetadata && isSignedEmbedToken(publicMetadata.token);

  const reportToolResultRef = useRef<ReportToolResult | null>(null);

  const apiUrl = useMemo(() => {
    if (!publicMetadata) {
      return `/api/backend/groups/${groupId}/conversation/${conversationId}/vercel-events`;
    }
    const params = new URLSearchParams({
      assistant_id: assistantId,
      group_id: groupId,
    });
    if (publicMetadata.deployment_id) {
      params.set("deployment_id", publicMetadata.deployment_id);
    }
    if (!isSignedEmbed) params.set("token", publicMetadata.token);
    return `/api/public/groups/${groupId}/conversation/${conversationId}/vercel-events?${params.toString()}`;
  }, [publicMetadata, groupId, conversationId, assistantId, isSignedEmbed]);

  const authToken = publicMetadata?.auth_token;
  // signed_token embeds carry the JWT in x-embed-token; noxus_user embeds relay
  // the real user's access token as a Bearer (the public proxy forwards it).
  const embedToken = isSignedEmbed ? publicMetadata?.token : undefined;

  // Names of every frontend tool declared to the agent in this session. A
  // deferred call for one of these MUST get an output (else the turn dangles
  // and reloads render it as a phantom approval request), so onToolCall
  // reports output-error when the tool is gone from the registry (page
  // changed). MCP/server tool names are never in this set.
  const sentFrontendToolNamesRef = useRef<Set<string>>(new Set());
  const trackedTransportBody = useMemo(() => {
    if (!transportBody) return undefined;
    return () => {
      const body = transportBody();
      body.frontend_tools?.forEach((t) =>
        sentFrontendToolNamesRef.current.add(t.name)
      );
      return body;
    };
  }, [transportBody]);

  // useChat captures the Chat instance at mount and only rebuilds it when the
  // `chat`/`id` option identity changes — neither of which we pass. A new
  // transport object would therefore be ignored, so the token has to reach the
  // wire through a ref that `headers` reads on every request instead.
  const authRef = useRef<ChatAuth>({ embedToken, authToken });
  authRef.current = { embedToken, authToken };
  const reconnectStreamReadyRef = useRef<() => void>(() => undefined);
  const reconnectUnavailableRef = useRef<() => void>(() => undefined);
  const replayCompletedRef = useRef(false);
  const streamCompletedRef = useRef(false);
  const showNewestOnFinishRef = useRef(false);
  // Every request, a tool or approval continuation included: the newest page
  // may only replace the list if none started since the compacted turn ended.
  const requestsRef = useRef(0);
  const showNewestRef = useRef<(lastId: string | undefined) => void>(
    () => undefined
  );

  const transport = useMemo(
    () =>
      new ReconnectAwareChatTransport(
        {
          api: apiUrl,
          // The stream never passes through the axios interceptors, so it turns
          // a 507 or 402 into its own error and raises the matching dialog.
          fetch: limitAwareFetch,
          headers: () => ({
            ...authHeaders(authRef.current),
            Accept: "text/event-stream",
          }),
          // Resume hits the same URL with GET, not the default
          // `${api}/${chatId}/stream` shape the SDK appends. Live recovery
          // may also replay a just-completed Redis stream; mount recovery may
          // not, because DB-loaded completed messages are already present.
          prepareReconnectToStreamRequest: ({ api }) => ({
            api: replayCompletedRef.current
              ? `${api}${api.includes("?") ? "&" : "?"}replay_completed=true`
              : api,
          }),
          ...(trackedTransportBody ? { body: trackedTransportBody } : {}),
          prepareSendMessagesRequest: ({
            id,
            messages,
            body,
            trigger,
            messageId,
          }) => {
            requestsRef.current += 1;
            return {
              body: {
                ...body,
                id,
                trigger,
                messageId,
                ...latestTurnOnly(messages),
              },
            };
          },
        },
        () => {
          streamCompletedRef.current = false;
        },
        () => reconnectStreamReadyRef.current(),
        () => reconnectUnavailableRef.current()
      ),
    // Tokens are deliberately not deps: they are read live from authRef, so a
    // refreshed JWT must not tear down the SSE stream mid-response.
    [apiUrl, trackedTransportBody]
  );

  const [reconnectBudget] = useState(() => new ReconnectBudget());
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectRef = useRef<(() => void) | null>(null);
  const pendingReconnectRef = useRef(false);
  const [finalAnswerReplay] = useState(createFinalAnswerReplay);
  const reconnectToastId = `chat-reconnect-${conversationId}`;

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const manualReconnect = useCallback(() => {
    reconnectBudget.reset();
    clearReconnectTimer();
    reconnectRef.current?.();
  }, [clearReconnectTimer, reconnectBudget]);

  const chat = useChat<NoxusUIMessage>({
    messages: initialMessages,
    transport,
    resume: true,
    experimental_throttle: 50,
    onFinish: ({ message, isAbort, isError }) => {
      finalAnswerReplay.onFinish(setMessages);
      // The SDK invokes onFinish after onError. Preserve the retry that
      // onError just scheduled instead of immediately clearing its timer.
      if (isError) return;
      if (pendingReconnectRef.current) {
        pendingReconnectRef.current = false;
        reconnectRef.current?.();
        return;
      }
      // A clean EOF without the backend's terminal marker is how several
      // corporate proxies surface a severed long-lived response.
      if (!isAbort && !streamCompletedRef.current) {
        reconnectRef.current?.();
        return;
      }
      reconnectBudget.reset();
      clearReconnectTimer();
      invalidateConversations();
      // A stopped turn keeps what streamed: the server saves the partial
      // reply only after the stop request, so its newest page would lack it.
      if (showNewestOnFinishRef.current && !isAbort) {
        showNewestRef.current(message.id);
      }
      showNewestOnFinishRef.current = false;
    },
    onError: (error) => {
      pendingReconnectRef.current = false;
      // A refused request is a hard stop, not a dropped connection: the dialog
      // is already up, so don't retry and don't claim the stream was
      // interrupted.
      if (isOutOfStorageError(error) || isBillingLimitError(error)) {
        reconnectBudget.reset();
        clearReconnectTimer();
        // A deployed widget has no limit modal to raise.
        if (isBillingLimitError(error) && publicMetadata) {
          SpotToast.error(t("usageLimitDescription"));
        }
        return;
      }
      // An expired embed JWT surfaces here as a plain request failure. Ask the
      // host for a fresh one first, otherwise every retry replays the dead token
      // and the user is told the connection dropped.
      onAuthRetry?.();
      const delay = reconnectBudget.next();
      if (delay !== null) {
        clearReconnectTimer();
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          reconnectRef.current?.();
        }, delay);
        return;
      }
      SpotToast.error(
        t("connectionLost"),
        { label: t("reconnect"), onClick: manualReconnect },
        Infinity,
        reconnectToastId
      );
    },
    onToolCall: ({ toolCall }: { toolCall: GenieToolCall }): void => {
      const report = reportToolResultRef.current;
      if (!report) return;
      // Questions are answered by the user through the question card.
      if (toolCall.toolName === QUESTION_TOOL_NAME) return;
      // Gated calls are only executed when the backend stamps them approved
      // (grant on first emission, or re-emission after the user allows);
      // unmarked ones are resolved through the approval flow instead.
      if (isGatedForApproval(toolCall)) return;
      void runGenieToolCall(toolCall, report, sentFrontendToolNamesRef.current);
    },
    onData: (dataPart) => {
      finalAnswerReplay.onData(dataPart);
      if (
        dataPart.type === "data-error" &&
        (dataPart.data as { code?: string } | undefined)?.code ===
          QUESTION_REJECTED_CODE
      ) {
        // The server refused the answer (already answered elsewhere, or the
        // step changed): say so, then show what the server holds instead of
        // resubmitting the same answer.
        SpotToast.error(
          (dataPart.data as { message?: string }).message ??
            tQuestions("answerNotApplied")
        );
        void reloadFromServer();
        return;
      }
      if (dataPart.type === "data-reconnect") {
        pendingReconnectRef.current = true;
        return;
      }
      if (dataPart.type === "data-stream_complete") {
        streamCompletedRef.current = true;
        return;
      }
      // A compacted history starts its newest page at this turn; an approval
      // another tab or a new message already settled leaves this tab behind.
      // Either way the list is reloaded from the server once the stream ends.
      if (
        dataPart.type === "data-history_compacted" ||
        dataPart.type === "data-approval_superseded"
      ) {
        showNewestOnFinishRef.current = true;
        return;
      }
      if (dataPart.type === "data-set_conversation_title") {
        invalidateConversations();
      } else if (dataPart.type === "data-clear_conversation") {
        setMessages([]);
      } else if (dataPart.type === "data-conversation_locked") {
        const data = (dataPart as { data?: { message?: string } }).data;
        setLockedMessage(data?.message ?? t("conversationLocked"));
      }
    },
    sendAutomaticallyWhen: shouldSendAutomatically,
  });
  const {
    messages,
    sendMessage,
    status,
    regenerate,
    stop,
    addToolOutput: reportToolResult,
    setMessages,
    addToolApprovalResponse,
    resumeStream,
    clearError,
  } = chat;
  reportToolResultRef.current = reportToolResult as ReportToolResult;

  const reloadFromServer = async (): Promise<void> => {
    const stored = storedUIMessages(await refetchConversation());
    if (stored) setMessages(stored);
  };
  const earlier = useEarlierMessages({
    history: conversationData?.history,
    groupId,
    conversationId,
    assistantId,
    publicMetadata,
    setMessages,
  });
  const markSend = useFirstTokenTiming(
    messages,
    status,
    isGenie ? "genie" : "agent"
  );

  // Latest messages, read at call time so handleStop and regenerateLatest are
  // not recreated on every streamed token.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Every Retry goes through here, whichever button asked: a reply in an
  // earlier turn cannot be regenerated (see latestTurnOnly).
  const regenerateLatest = useCallback(
    (options?: { messageId?: string }) => {
      if (
        options?.messageId &&
        !inLatestTurn(messagesRef.current, options.messageId)
      ) {
        return;
      }
      return regenerate(options);
    },
    [regenerate]
  );
  showNewestRef.current = (lastId) => {
    const requests = requestsRef.current;
    void earlier.showNewest(lastId, () => requestsRef.current === requests);
  };
  // Genie wrote to this chat from its own panel: show what it wrote, once
  // this chat's own turn, if one is streaming or about to reconnect, has
  // ended.
  const genieChange = useAtomValue(genieWroteConversationAtom);
  const genieChangeShown = useRef(genieChange);
  const settled =
    status === "ready" || (status === "error" && !reconnectTimerRef.current);
  useEffect(() => {
    if (
      !genieChange ||
      genieChange === genieChangeShown.current ||
      genieChange.conversationId !== conversationId ||
      !settled
    )
      return;
    genieChangeShown.current = genieChange;
    showNewestRef.current(messagesRef.current.at(-1)?.id);
  }, [genieChange, conversationId, settled]);
  reconnectStreamReadyRef.current = () => {
    // The stream is back: the next drop starts a fresh count, and once the
    // turn ends it is read back from the server, which holds all of it, not
    // only what the replay carried.
    reconnectBudget.recovered();
    showNewestOnFinishRef.current = true;
    setMessages((msgs) => {
      const last = msgs[msgs.length - 1];
      return last && last.role === "assistant" ? msgs.slice(0, -1) : msgs;
    });
  };
  reconnectUnavailableRef.current = () => {
    // A live retry returning 204 means there is no replayable active stream.
    // Keep the partial message, release the SDK's error latch, and refresh the
    // persisted conversation rather than leaving the composer frozen.
    if (!replayCompletedRef.current) return;
    reconnectBudget.reset();
    clearReconnectTimer();
    clearError();
    invalidateConversations();
  };

  useToolEffects(messages as unknown as UIMessage[], conversationId, {
    navigate: !!isGenie,
  });

  useEffect(() => {
    reconnectRef.current = () => {
      streamCompletedRef.current = false;
      replayCompletedRef.current = true;
      void resumeStream().finally(() => {
        replayCompletedRef.current = false;
      });
    };
  }, [resumeStream]);

  useEffect(() => clearReconnectTimer, [clearReconnectTimer]);

  const [isStopping, setIsStopping] = useState(false);
  const stopRequestedRef = useRef(false);

  // Release the interrupt guard once the stream settles so the Stop button
  // works again on the next turn.
  useEffect(() => {
    if (status === "ready" || status === "error") {
      stopRequestedRef.current = false;
      setIsStopping(false);
    }
  }, [status]);

  const handleStop = useCallback(() => {
    // Debounce rapid clicks: a synchronous ref guard swallows a second click
    // before the disabled-button re-render lands, so we don't fire a
    // duplicate stop()/`/stop` for one interrupt.
    if (stopRequestedRef.current) return;
    stopRequestedRef.current = true;
    setIsStopping(true);

    const msgs = messagesRef.current;
    const last = msgs[msgs.length - 1];
    const generatedContent =
      last?.role === "assistant" && hasPersistableTurnContent(last.parts);

    // Abort the client stream. When the assistant already produced text or a
    // completed tool call, the Vercel SDK keeps that partial in `messages` and
    // the worker persists it, so the interrupted reply survives reload — leave
    // it on screen.
    stop();

    // Nothing was generated yet: don't leave a hanging prompt. Put the text
    // back in the composer and drop the un-answered message from the transcript
    // (the worker deletes the row server-side via /stop).
    if (!generatedContent) {
      const lastUserIndex = msgs.findLastIndex((m) => m.role === "user");
      if (lastUserIndex !== -1) {
        setText(getTextFromParts(msgs[lastUserIndex].parts));
        setMessages((prev) => prev.slice(0, lastUserIndex));
      }
    }

    // Tell the backend to abort the worker task. On failure, refetch to
    // restore consistent UI state.
    const request = publicMetadata
      ? PublicConversationService.stopConversation({
          spotGroupId: groupId,
          conversationId,
          assistantId,
          token: publicMetadata.token,
          deploymentId: publicMetadata.deployment_id,
          authToken: publicMetadata.auth_token,
        })
      : fetch(
          `/api/backend/groups/${groupId}/conversation/${conversationId}/stop`,
          { method: "POST" }
        );
    void request.then(invalidateConversations, invalidateConversations);
  }, [
    stop,
    setMessages,
    setText,
    groupId,
    conversationId,
    assistantId,
    publicMetadata,
    invalidateConversations,
  ]);

  // The composer sits outside the conversation's scroll context: sending or
  // answering a card follows the turn it starts, wherever the reader was.
  const conversationScroll = useRef<StickToBottomContext>(null);
  const followLatest = useCallback(() => {
    void conversationScroll.current?.scrollToBottom();
  }, []);

  const handleSendMessage = (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>
  ) => {
    const isButtonClick =
      event.nativeEvent instanceof SubmitEvent &&
      event.nativeEvent.submitter instanceof HTMLButtonElement;

    if (status === "streaming" || status === "submitted") {
      if (isButtonClick) {
        handleStop();
      }
      return;
    }

    if (message.text.trim() === "" && message.files?.length === 0) {
      return;
    }

    if (status === "error") {
      // Drop the dead turn's error before sending, otherwise the chat stays
      // latched in its error state and the composer never recovers.
      clearError();
    }
    streamCompletedRef.current = false;
    pendingReconnectRef.current = false;
    markSend();
    sendMessage({
      text: message.text,
      files: message.files,
    });
    setText("");
    followLatest();
  };

  // Built-in tool step labels arrive in English; in another language they are
  // swapped for their translation before anything below reads them.
  const allMessages = useLocalizedToolLabels(messages);

  // sender_ref is now stamped on each UIMessage's ``metadata`` by the
  // backend ``dump_messages_for_display``. We used to index into
  // ``conversationData.messages`` by position, but that breaks whenever a
  // single DB row dumps to multiple UI messages (e.g. user prompt +
  // assistant response in one row) — the second UI message would borrow the
  // next DB row's sender, which is exactly the "wrong user" bug.
  const channelRef = conversationData?.channel_ref as
    | { channel_type?: string }
    | undefined;

  const isPublic = !!publicMetadata;
  const isWidget = isPublic;
  const displayOptions = useChatDisplayOptions(
    assistantData,
    isPublic,
    !!isCompact,
    {
      showMessageFeedback,
      readOnly,
      offerTestThisReply,
      rowsShowChanges: !!isGenie,
    }
  );

  // When CoT grouping is on, fold consecutive assistant UI messages into one
  // so a chain run can span message boundaries (otherwise we get back-to-back
  // CoT collapsibles for what is conceptually one assistant turn).
  const displayMessages = useMemo<
    { msg: NoxusUIMessage; originalIndex: number }[]
  >(() => {
    if (!displayOptions.groupIntoChainOfThought) {
      return allMessages.map((msg, originalIndex) => ({ msg, originalIndex }));
    }
    return foldAssistantMessages(allMessages);
  }, [allMessages, displayOptions.groupIntoChainOfThought]);

  const displayMessagesList = useMemo(
    () => displayMessages.map((d) => d.msg),
    [displayMessages]
  );

  const lastMessage = allMessages[allMessages.length - 1];
  const turnUnstarted = isTurnUnstarted(status, lastMessage);
  const waitLabel = thinkingLabel(displayOptions);

  // While a tool call waits on the user's approve/deny decision, the composer
  // is replaced by an inline approval prompt (Claude-Code style), so a new
  // message can't be sent past an unresolved tool gate.
  const approval = useApproval(chat, followLatest);
  // Questions come before approvals: the step continues only once both are
  // answered, and the answers often decide what the approval is for.
  const questions = useQuestions({
    messages: chat.messages,
    addToolOutput: reportToolResult,
  });
  const [setAsideQuestionId, setSetAsideQuestionId] = useState<string | null>(
    null
  );
  const showQuestions =
    !!questions && setAsideQuestionId !== questions.call.toolCallId;

  const followOnMount = useCallback(
    (card: HTMLDivElement | null) => {
      if (card) followLatest();
    },
    [followLatest]
  );

  // The scroll area only follows its content growing. A taller composer (an
  // approval card, its rows loading) shrinks it from below instead, which
  // would hide the latest text behind the composer.
  const keepLatestInView = useCallback((composer: HTMLDivElement) => {
    const observer = new ResizeObserver(() => {
      const scroll = conversationScroll.current;
      if (scroll?.state.isAtBottom) void scroll.scrollToBottom("instant");
    });
    observer.observe(composer);
    return () => observer.disconnect();
  }, []);

  return (
    <ChatWidgetContext.Provider
      value={{
        isPublic,
        publicAuth: publicMetadata
          ? {
              spotGroupId: groupId,
              assistantId,
              token: publicMetadata.token,
              deploymentId: publicMetadata.deployment_id,
              authToken: publicMetadata.auth_token,
            }
          : undefined,
      }}
    >
      <ChatDisplayOptionsContext.Provider value={displayOptions}>
        <div className="flex h-full w-full relative justify-center">
          <DebugSheetHost conversationId={conversationId} />
          <div className="flex flex-col h-full w-full bg-background mx-auto">
            <Conversation
              className="flex flex-1 min-h-0 flex-col"
              contextRef={conversationScroll}
            >
              <div className="min-h-0 flex-1">
                <ConversationContent
                  className={cn(
                    chatColumnClasses({
                      isCompact,
                      isGenie,
                      isTextExpanded,
                      isWidget,
                    }),
                    "relative mx-auto",
                    isGenie ? "pt-0 pb-6" : "pt-3 pb-6 px-9",
                    (isWidget || isGenie) && "px-7"
                  )}
                  compact={isCompact}
                >
                  <AnimatePresence>
                    {allMessages.length === 0 ? (
                      <NewChatEmptyState
                        assistant={assistantData}
                        groupId={groupId}
                        isPublic={isPublic}
                        agentIconUrl={agentIconUrl}
                        isGenie={isGenie}
                      />
                    ) : (
                      <ChatMessageList
                        displayMessages={displayMessages}
                        displayMessagesList={displayMessagesList}
                        earlier={earlier}
                        canLoadEarlier={
                          status === "ready" || status === "error"
                        }
                        assistantId={assistantId}
                        groupId={groupId}
                        conversationId={conversationId}
                        regenerate={regenerateLatest}
                        status={status}
                        setSourcesDrawerOpen={setSourcesDrawerOpen}
                        publicMetadata={publicMetadata}
                        channelType={channelRef?.channel_type}
                        isGenie={isGenie}
                        isTurnUnstarted={turnUnstarted}
                        waitLabel={waitLabel}
                      />
                    )}
                  </AnimatePresence>
                </ConversationContent>
              </div>
              <ScrollToLatest />
            </Conversation>

            <div
              ref={keepLatestInView}
              className={cn(
                chatColumnClasses({
                  isCompact,
                  isGenie,
                  isTextExpanded,
                  isWidget,
                }),
                "py-0 bg-background self-center"
              )}
            >
              {readOnly ? (
                <div className="mb-6 flex flex-wrap items-center justify-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm">
                  <span className="text-muted-foreground">
                    You're viewing this conversation in read-only mode.
                  </span>
                  {onResume && (
                    <Button variant="outline" onClick={onResume}>
                      <MessageSquare className="size-4" />
                      Resume conversation
                    </Button>
                  )}
                </div>
              ) : showQuestions && questions ? (
                <div
                  key={questions.call.toolCallId}
                  ref={followOnMount}
                  className="pb-3"
                >
                  <QuestionComposer
                    active={questions}
                    onSendDifferentMessage={() =>
                      setSetAsideQuestionId(questions.call.toolCallId)
                    }
                  />
                </div>
              ) : approval ? (
                // Match the prompt input's bottom offset (pb-8 / powered-by
                // row) so the card doesn't sit lower than the input it swaps.
                <div
                  key={approval.pending.approvalId}
                  ref={followOnMount}
                  className="pb-3"
                >
                  <ApprovalComposer approval={approval} />
                </div>
              ) : (
                <>
                  {questions && !showQuestions && (
                    <div className="flex justify-end pb-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSetAsideQuestionId(null)}
                      >
                        {tQuestions("backToQuestions")}
                      </Button>
                    </div>
                  )}
                  <NoxusPromptInput
                    text={text}
                    setText={setText}
                    status={status}
                    handleSubmit={handleSendMessage}
                    assistantData={assistantData}
                    groupId={groupId}
                    conversationId={conversationId}
                    configChanged={configChanged}
                    onStartNewChat={onStartNewChat}
                    isPublic={!!publicMetadata}
                    todos={todos}
                    todoPanelOpen={todoPanelOpen}
                    onTodoPanelOpenChange={handleTodoPanelOpenChange}
                    onStop={handleStop}
                    stopDisabled={isStopping}
                    lockedMessage={lockedMessage}
                    warningMessage={warningMessage}
                    showPoweredBy={showPoweredBy}
                    isGenie={isGenie}
                    focusRequest={draftFocus}
                  />
                </>
              )}
            </div>
          </div>
          <SourcesDrawer
            sources={sourcesDrawerOpen}
            onClose={() => setSourcesDrawerOpen(null)}
            floating={sourcesMode === "floating"}
            sourcesClassName={sourcesClassName}
          />
        </div>
      </ChatDisplayOptionsContext.Provider>
    </ChatWidgetContext.Provider>
  );
};

// The rating already rides along on the DB messages of the same response:
// carry it onto the UI messages so a reload shows the thumb the user picked.
function storedUIMessages(data: unknown): NoxusUIMessage[] | undefined {
  const stored = data as
    | {
        vercel_messages?: NoxusUIMessage[];
        messages?: { id: string; is_positive?: boolean | null }[];
      }
    | undefined;
  if (!stored?.vercel_messages) return undefined;
  return withStoredFeedback(stored.vercel_messages, stored.messages);
}
